import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { DeliveryChecklistForm } from './DeliveryChecklistForm';

interface Props {
  tenantId: string;
  propertyId: string;
}

export function DeliveryInspectionSection({ tenantId }: Props) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canManageRentals = hasPermission('update_rentals') || hasPermission('manage_rentals');
  const [selectedTechnician, setSelectedTechnician] = useState('');
  const [showAssignForm, setShowAssignForm] = useState(false);

  const { data: inspection, isLoading } = useQuery({
    queryKey: ['delivery-inspection', tenantId],
    queryFn: () => api.getDeliveryInspection(tenantId),
  });

  const { data: technicians = [] } = useQuery({
    queryKey: ['maintenance-or-admin-users'],
    queryFn: () => api.getMaintenanceOrAdminUsers(),
    enabled: showAssignForm,
  });

  const createMutation = useMutation({
    mutationFn: () => api.createDeliveryInspection(tenantId, selectedTechnician),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['delivery-inspection', tenantId] });
      await qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
      setShowAssignForm(false);
      setSelectedTechnician('');
    },
  });

  if (isLoading) return <p className="text-sm text-gray-400">Cargando...</p>;

  if (inspection && inspection.status !== 'completed') {
    if (inspection.assignedUserId === user?.id || canManageRentals) {
      return (
        <div className="mt-4">
          <DeliveryChecklistForm
            inspectionId={inspection.id}
            assignedUserName={inspection.assignedUser?.name}
            onCompleted={async () => {
              await qc.invalidateQueries({ queryKey: ['delivery-inspection', tenantId] });
              await qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
            }}
          />
        </div>
      );
    }

    const statusLabel: Record<string, string> = {
      pending: 'Pendiente — esperando al técnico',
      in_progress: 'En progreso — el técnico está inspeccionando',
    };
    return (
      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm font-medium text-blue-800">Inspección de entrega</p>
        <p className="text-sm text-blue-600 mt-1">
          Asignado a: <strong>{inspection.assignedUser?.name}</strong>
        </p>
        <p className="text-sm text-blue-600">Estado: {statusLabel[inspection.status] || inspection.status}</p>
      </div>
    );
  }

  if (!showAssignForm) {
    return (
      <div className="mt-4">
        <Button type="button" onClick={() => setShowAssignForm(true)}>
          Asignar inspección de entrega
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-4 p-4 bg-gray-50 border rounded-lg space-y-3">
      <p className="text-sm font-medium">Asignar técnico para inspección de entrega</p>
      <select
        value={selectedTechnician}
        onChange={(e) => setSelectedTechnician(e.target.value)}
        className="w-full border rounded-lg px-3 py-2 text-sm"
      >
        <option value="">Seleccionar técnico...</option>
        {technicians.map((u: any) => (
          <option key={u.id} value={u.id}>
            {u.name}
          </option>
        ))}
      </select>
      <div className="flex gap-2">
        <Button
          type="button"
          onClick={() => createMutation.mutate()}
          disabled={!selectedTechnician || createMutation.isPending}
        >
          {createMutation.isPending ? 'Asignando...' : 'Asignar'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setShowAssignForm(false);
            setSelectedTechnician('');
          }}
        >
          Cancelar
        </Button>
      </div>
    </div>
  );
}
